/**
 * Centralized Snapshot Search Utility — Options Oracle V4
 * 
 * Provides robust, unified functions for:
 * 1. Extracting HH:mm from any snapshot's timestampIST field (no double-offset)
 * 2. Finding the best matching snapshot in a history array
 * 3. Extracting options data from a snapshot (handles all data formats)
 * 4. Computing available times from a history array
 */
import { getOptionsForExpiry } from './data-utils'

// ─── Time Helpers ──────────────────────────────────────────────────────────────

/**
 * Convert "HH:mm" or "HH:mm AM/PM" to minutes since midnight.
 * Pure function, no side effects.
 */
export function timeToMin(timeStr: any): number {
    if (!timeStr) return 0
    const str = String(timeStr).trim()
    const parts = str.split(' ')
    const time = parts[0]
    const period = parts[1]

    if (!time) return 0
    const [h, m] = time.split(':').map(Number)
    if (h === undefined || m === undefined || isNaN(h) || isNaN(m)) return 0

    let hours = h
    if (period) {
        const p = period.toUpperCase()
        if (p === 'PM' && hours !== 12) hours += 12
        if (p === 'AM' && hours === 12) hours = 0
    }
    return hours * 60 + m
}

/**
 * Convert minutes since midnight to "HH:mm" string.
 */
export function minToTime(minutes: number): string {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// ─── Timestamp Extraction ──────────────────────────────────────────────────────

/**
 * Extract the HH:mm time part from a snapshot's timestampIST field.
 * 
 * CRITICAL: Does NOT call toIST() to avoid double-offset on already-IST strings.
 * Handles all known formats:
 * - ISO string: "2026-04-04T09:16:00+05:30"
 * - ISO without offset: "2026-04-04T09:16:00"  
 * - Space-separated: "2026-04-04 09:16:00"
 * - Epoch number: 1743745500000 (converts to IST)
 * - Already HH:mm: "09:16"
 */
export function extractTimePart(timestampIST: any): string {
    if (!timestampIST) return ''

    const raw = timestampIST

    // Case 1: Numeric epoch → convert to IST time
    if (typeof raw === 'number' || (typeof raw === 'string' && !isNaN(Number(raw)) && Number(raw) > 946684800000)) {
        const epoch = typeof raw === 'number' ? raw : Number(raw)
        // IST is UTC + 5:30 = +19800000 ms
        const d = new Date(epoch + 19800000)
        const h = d.getUTCHours()
        const m = d.getUTCMinutes()
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    }

    const tsStr = String(raw).trim()

    // Case 1b: UTC ISO String (e.g. "2026-08-23T04:00:00.000Z" or "2026-08-23T04:00:00Z" or "+00:00")
    if (tsStr.includes('Z') || tsStr.includes('+00:00') || (tsStr.includes('T') && tsStr.endsWith('Z'))) {
        const epoch = new Date(tsStr).getTime()
        if (!isNaN(epoch)) {
            const d = new Date(epoch + 19800000)
            const h = d.getUTCHours()
            const m = d.getUTCMinutes()
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        }
    }

    // Case 2: String timestamp with explicit offset (e.g. "2026-08-23T09:30:00+05:30")
    // Or plain time strings like "11:45", "9:15 AM", "2026-04-04 15:30:00", "04/04/2026 2:15 PM"
    const timeMatch = tsStr.match(/(\d{1,2}:\d{2})(?::\d{2})?(?:\.\d+)?\s*(AM|PM|am|pm)?/)
    
    if (timeMatch && timeMatch[1]) {
        let [hStr, mStr] = timeMatch[1].split(':')
        let h = parseInt(hStr || '0', 10)
        let m = mStr || '00'
        const period = timeMatch[2] ? timeMatch[2].toUpperCase() : ''
        
        if (period === 'PM' && h !== 12) h += 12
        if (period === 'AM' && h === 12) h = 0

        // Always return clean HH:mm strictly in 24hr format
        return `${String(h).padStart(2, '0')}:${m}`
    }

    return ''
}

/**
 * Extract time as minutes from a snapshot.
 */
export function extractTimeMinutes(snap: any): number {
    const rawTime = snap?.timestampIST || snap?.timestamp_ist || snap?.time || snap?.timestamp || snap?.TimeStamp || snap?.Time || snap?.Date || snap?.date || snap?.datetime
    if (!rawTime) return NaN
    const tp = extractTimePart(rawTime)
    if (!tp) return NaN
    return timeToMin(tp)
}

// ─── Options Extraction ────────────────────────────────────────────────────────

/**
 * Extract options array from a snapshot. Handles all known data formats:
 * 1. optionsData[symbol][expiry] (CSV/processed)
 * 2. optionsData[anySymbol][anyExpiry] (fallback)
 * 3. Direct arrays: optionChain, options, chain
 * 4. Raw Upstox strikeMap format
 */
// PERF CACHE: Both useCogEngine and useMarketWatchEngine parse identical snapshots.
// WeakMap keyed by snapshot object → Map<symbol|expiry, options[]>.
// Since snapshots are the same object references from Zustand, this eliminates ~50% of parse time.
const _extractCache = new WeakMap<object, Map<string, any[]>>()

export function extractSnapshotOptions(snap: any, symbol: string, expiry: string): any[] {
    if (!snap) return []

    // Check cache first
    const cacheKey = `${symbol}|${expiry}`
    let snapCache = _extractCache.get(snap)
    if (snapCache) {
        const cached = snapCache.get(cacheKey)
        if (cached) return cached
    }

    // Helper to save to cache and return with guaranteed strike deduplication
    const saveAndReturn = (result: any[]) => {
        let deduped = result
        if (Array.isArray(result) && result.length > 1) {
            const seen = new Set<string>()
            const clean: any[] = []
            for (const item of result) {
                if (!item) continue
                const s = Number(item.strike ?? item.strikePrice ?? 0)
                const type = String(item.type || item.optionType || '').toUpperCase()
                const key = type ? `${s}_${type}` : `${s}`
                if (s > 0 && !seen.has(key)) {
                    seen.add(key)
                    clean.push(item)
                } else if (s <= 0) {
                    clean.push(item)
                }
            }
            deduped = clean
        }

        if (!snapCache) {
            snapCache = new Map()
            _extractCache.set(snap, snapCache)
        }
        snapCache.set(cacheKey, deduped)
        return deduped
    }

    // Strategy 0: rawOpts from parseHFParquet / parseHFCSV / parseIntradayCSV worker output
    if (Array.isArray(snap.rawOpts) && snap.rawOpts.length > 0) return saveAndReturn(snap.rawOpts)
    
    // Parquet worker uses optionsData format (Strategy 1), not rawOpts — this is expected

    // Strategy 1: Try optionsData[symbol][expiry]
    if (snap.optionsData) {
        let rawOpts = getOptionsForExpiry(snap.optionsData, symbol, expiry)
        if (rawOpts && rawOpts.length > 0) return saveAndReturn(rawOpts)

        // Fallback: try any available symbol/expiry key
        for (const sk of Object.keys(snap.optionsData)) {
            for (const ek of Object.keys(snap.optionsData[sk] || {})) {
                const arr = snap.optionsData[sk][ek]
                if (Array.isArray(arr) && arr.length > 0) return saveAndReturn(arr)
            }
        }
    }

    // Strategy 2: Direct arrays
    const directArr = snap.optionChain || snap.options || snap.chain
    if (Array.isArray(directArr) && directArr.length > 0) return saveAndReturn(directArr)

    // Strategy 3: Raw Upstox strikeMap (might be in snap directly or inside rawOpts if D1 fallback)
    const rawData = snap?.strategyChainData || snap?.data?.strategyChainData || snap?.rawOpts?.strategyChainData || snap?.rawOpts?.data?.strategyChainData
    const strikeMap = rawData?.strikeMap || {}
    if (Object.keys(strikeMap).length > 0) {
        const opts: any[] = []
        for (const [strikeStr, strikeData] of Object.entries(strikeMap)) {
            const strike = parseFloat(strikeStr)
            if (isNaN(strike)) continue
            const sd = strikeData as any
            const ce = sd?.callOptionData?.marketData
            const pe = sd?.putOptionData?.marketData
            const cAn = sd?.callOptionData?.analytics || {}
            const pAn = sd?.putOptionData?.analytics || {}
            opts.push({
                strike,
                callPrice: ce?.ltp || 0, putPrice: pe?.ltp || 0,
                callClose: ce?.ltp || 0, putClose: pe?.ltp || 0,
                callLtp: ce?.ltp || 0, putLtp: pe?.ltp || 0,
                callOpenInterest: ce?.oi || 0, putOpenInterest: pe?.oi || 0,
                callVolume: ce?.volume || 0, putVolume: pe?.volume || 0,
                callChangePercent: ce?.cp ? ((ce.ltp - ce.cp) / ce.cp) * 100 : 0,
                putChangePercent: pe?.cp ? ((pe.ltp - pe.cp) / pe.cp) * 100 : 0,
                callImpliedVolatility: cAn.iv || 0, putImpliedVolatility: pAn.iv || 0,
                callDelta: cAn.delta || 0, putDelta: pAn.delta || 0,
                callOpenInterestChange: (ce?.oi || 0) - (ce?.prevOi || ce?.oi || 0),
                putOpenInterestChange: (pe?.oi || 0) - (pe?.prevOi || pe?.oi || 0),
            })
        }
        opts.sort((a, b) => a.strike - b.strike)
        return saveAndReturn(opts)
    }

    return []
}

/**
 * Extract spot price from a snapshot.
 */
export function extractSpotPrice(snap: any, symbol?: string): number {
    if (!snap) return 0
    let spot = Number(snap.spotPrice || snap.indexLtp || snap.index_ltp || snap.Index_lastPrice || snap.underlyingPrice || snap.lastPrice || snap.ltp || snap.data?.ltp || 0)
    if (spot > 0) return spot

    // Try snap.indexData
    if (snap.indexData && typeof snap.indexData === 'object') {
        if (symbol) {
            const symUpper = symbol.toUpperCase()
            const symKey = Object.keys(snap.indexData).find(k => {
                const kUpper = k.toUpperCase()
                return kUpper.includes(symUpper) || symUpper.includes(kUpper)
            })
            if (symKey && snap.indexData[symKey]) {
                const idx = snap.indexData[symKey]
                spot = Number(idx?.lastPrice || idx?.ltp || idx?.spotPrice || idx?.close || 0)
                if (spot > 0) return spot
            }
        }
        const firstIdx = Object.values(snap.indexData)[0] as any
        if (firstIdx) {
            spot = Number(firstIdx?.lastPrice || firstIdx?.ltp || firstIdx?.spotPrice || firstIdx?.close || 0)
            if (spot > 0) return spot
        }
    }

    // Try optionChainExtendedData
    spot = Number(
        snap.optionChainExtendedData?.assetLtpc?.ltp || 
        snap?.data?.optionChainExtendedData?.assetLtpc?.ltp || 
        snap?.data?.strategyChainData?.assetLtpc?.ltp || 0
    )
    if (spot > 0) return spot

    return 0
}

// ─── Snapshot Search ───────────────────────────────────────────────────────────

export interface SnapshotResult {
    options: any[]
    spotPrice: number
    atmStrike: number
    timestamp: string
}

// ─── Fast History Minute Index Cache ──────────────────────────────────────────
export interface HistoryIndex {
    sortedMins: number[]
    minToSnap: Map<number, any>
    availableTimes: string[]
}

const _historyIndexCache = new WeakMap<object, HistoryIndex>()

export function getHistoryIndex(history: any[]): HistoryIndex {
    const cached = _historyIndexCache.get(history)
    if (cached) return cached

    const minToSnap = new Map<number, any>()
    const sortedMins: number[] = []
    const timeSet = new Set<string>()

    for (const snap of history) {
        const m = extractTimeMinutes(snap)
        if (!isNaN(m) && m > 0) {
            if (!minToSnap.has(m)) {
                sortedMins.push(m)
            }
            minToSnap.set(m, snap)
            const tp = extractTimePart(snap?.timestampIST || snap?.timestamp_ist || snap?.time || snap?.timestamp)
            if (tp) timeSet.add(tp)
        }
    }

    sortedMins.sort((a, b) => a - b)
    const availableTimes = Array.from(timeSet).sort()

    const index: HistoryIndex = { sortedMins, minToSnap, availableTimes }
    _historyIndexCache.set(history, index)
    return index
}

/**
 * Find the best matching snapshot for a target time using O(1) index & O(log N) binary search.
 */
export function findSnapshotAtTime(
    history: any[] | undefined,
    targetMin: number,
    symbol: string,
    expiry: string,
): SnapshotResult | null {
    if (!history || history.length === 0) return null

    const index = getHistoryIndex(history)
    let bestSnap = index.minToSnap.get(targetMin)

    if (!bestSnap && index.sortedMins.length > 0) {
        // Fast binary search for closest minute
        let left = 0
        let right = index.sortedMins.length - 1
        let bestMin = index.sortedMins[0]!
        let bestDiff = Math.abs(bestMin - targetMin)

        while (left <= right) {
            const mid = (left + right) >> 1
            const midMin = index.sortedMins[mid]!
            const diff = Math.abs(midMin - targetMin)

            if (diff < bestDiff) {
                bestDiff = diff
                bestMin = midMin
            }

            if (midMin === targetMin) {
                bestMin = midMin
                break
            } else if (midMin < targetMin) {
                left = mid + 1
            } else {
                right = mid - 1
            }
        }
        bestSnap = index.minToSnap.get(bestMin)
    }

    if (!bestSnap) return null

    const opts = extractSnapshotOptions(bestSnap, symbol, expiry)
    if (opts.length === 0) return null

    let atmStrike = bestSnap.atmStrike || bestSnap.analysis?.atmStrike || 0
    if (atmStrike <= 0) {
        const sp = extractSpotPrice(bestSnap, symbol)
        if (sp > 0 && opts.length >= 2) {
            const sortedStrikes = opts.map((o: any) => o.strike || o.strikePrice || 0).filter((s: number) => s > 0).sort((a: number, b: number) => a - b)
            let strikeGap = 50
            if (sortedStrikes.length >= 2) {
                const diffs: Record<number, number> = {}
                for (let i = 1; i < Math.min(20, sortedStrikes.length); i++) {
                    const d = sortedStrikes[i] - sortedStrikes[i - 1]
                    if (d > 0) diffs[d] = (diffs[d] || 0) + 1
                }
                const bestGap = Object.entries(diffs).sort((a, b) => b[1] - a[1])[0]
                if (bestGap) strikeGap = Number(bestGap[0]) || 50
            }
            atmStrike = Math.round(sp / strikeGap) * strikeGap
        }
    }

    return {
        options: opts,
        spotPrice: extractSpotPrice(bestSnap, symbol),
        atmStrike,
        timestamp: bestSnap.timestampIST || '',
    }
}

/**
 * Specifically lookup the 09:16 snapshot based on precise second bounds:
 * "consider 09:16 data from 09:15:40 to 09:16:55 whatever data is available in this range"
 */
export function find0916Snapshot(history: any[] | undefined): any {
    if (!history || history.length === 0) return null;

    const extractSeconds = (snap: any): number => {
        const rawTime = snap?.timestampIST || snap?.timestamp_ist || snap?.time || snap?.timestamp;
        if (!rawTime) return -1;
        
        if (typeof rawTime === 'number' || (!isNaN(Number(rawTime)) && Number(rawTime) > 946684800000)) {
            // PERFORMANCE FIX: Avoid toLocaleTimeString inside a tight loop!
            // IST is UTC + 5:30 = +19800000 ms
            const d = new Date(Number(rawTime) + 19800000);
            const h = d.getUTCHours();
            const m = d.getUTCMinutes();
            const s = d.getUTCSeconds();
            return h * 3600 + m * 60 + s;
        }
        
        const str = String(rawTime).trim();
        const match = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (match) {
            let h = parseInt(match[1] || '0', 10);
            let m = parseInt(match[2] || '0', 10);
            let s = parseInt(match[3] || '0', 10);
            if (str.toUpperCase().includes('PM') && h !== 12) h += 12;
            if (str.toUpperCase().includes('AM') && h === 12) h = 0;
            return h * 3600 + m * 60 + s;
        }
        return -1;
    };

    const targetMinSec = 9 * 3600 + 15 * 60 + 40; // 09:15:40 = 33340
    const targetMaxSec = 9 * 3600 + 17 * 60 + 55; // 09:17:55 = 33475
    const exactTarget = 9 * 3600 + 16 * 60;       // 09:16:00 = 33360
    
    const validSnaps = history.filter(snap => {
        const sec = extractSeconds(snap);
        return sec >= targetMinSec && sec <= targetMaxSec;
    });

    if (validSnaps.length === 0) {
        // Fallback: If no snapshot is found in the strict 09:15-09:17 window, 
        // just return the earliest available snapshot in the history to prevent baseSnapshot from being null.
        return history[0] || null;
    }

    return validSnaps.reduce((prev, curr) => {
        return Math.abs(extractSeconds(curr) - exactTarget) < Math.abs(extractSeconds(prev) - exactTarget) ? curr : prev;
    });
}

/**
 * Find snapshot at time using PREVIOUS-only strategy (for range end-time lookups).
 * Returns the latest snapshot at or before the target time.
 */
export function findSnapshotAtOrBefore(
    history: any[] | undefined,
    targetMin: number,
    symbol: string,
    expiry: string,
): SnapshotResult | null {
    if (!history || history.length === 0) return null

    const index = getHistoryIndex(history)
    let bestSnap = index.minToSnap.get(targetMin)

    if (!bestSnap && index.sortedMins.length > 0) {
        // Binary search for greatest minute <= targetMin
        let left = 0
        let right = index.sortedMins.length - 1
        let bestMin: number | null = null

        while (left <= right) {
            const mid = (left + right) >> 1
            const midMin = index.sortedMins[mid]!

            if (midMin <= targetMin) {
                bestMin = midMin
                left = mid + 1 // try to find larger minute still <= targetMin
            } else {
                right = mid - 1
            }
        }

        if (bestMin !== null) {
            bestSnap = index.minToSnap.get(bestMin)
        }
    }

    if (!bestSnap) return null

    const opts = extractSnapshotOptions(bestSnap, symbol, expiry)
    if (opts.length === 0) return null

    // Compute ATM from spotPrice if not stored in the snapshot
    let atmStrike = bestSnap.atmStrike || bestSnap.analysis?.atmStrike || 0
    if (atmStrike <= 0) {
        const sp = extractSpotPrice(bestSnap, symbol)
        if (sp > 0 && opts.length >= 2) {
            const sortedStrikes = opts.map((o: any) => o.strike || o.strikePrice || 0).filter((s: number) => s > 0).sort((a: number, b: number) => a - b)
            let strikeGap = 50
            if (sortedStrikes.length >= 2) {
                const diffs: Record<number, number> = {}
                for (let i = 1; i < Math.min(20, sortedStrikes.length); i++) {
                    const d = sortedStrikes[i] - sortedStrikes[i - 1]
                    if (d > 0) diffs[d] = (diffs[d] || 0) + 1
                }
                const bestGap = Object.entries(diffs).sort((a, b) => b[1] - a[1])[0]
                if (bestGap) strikeGap = Number(bestGap[0]) || 50
            }
            atmStrike = Math.round(sp / strikeGap) * strikeGap
        }
    }

    return {
        options: opts,
        spotPrice: extractSpotPrice(bestSnap, symbol),
        atmStrike,
        timestamp: bestSnap.timestampIST || '',
    }
}

/**
 * Extract sorted unique HH:mm time strings from a history array.
 * Uses cached index for instant return.
 */
export function getAvailableTimes(history: any[] | undefined): string[] {
    if (!history || history.length === 0) return []
    const timeSet = new Set<string>()
    for (const snap of history) {
        const tp = extractTimePart(snap?.timestampIST)
        if (tp) timeSet.add(tp)
    }
    return Array.from(timeSet).sort()
}
